import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, FileText, Building2, User, Package, ClipboardList, Ship, ExternalLink, Trash2 } from "lucide-react";
import StockPoDetail from "@/components/StockPoDetail";

const STEPS = [["open", "Open"], ["in_production", "In Production"], ["supplier_collected", "Supplier Collected"], ["in_transit", "In Transit"], ["delivered", "Delivered"], ["complete", "Complete"]];
const money = (cur, v) => (v || v === 0) ? `${/usd/i.test(cur || "") ? "USD" : "TSh"} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—";
const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—"; } catch { return "—"; } };
const ATT = [["client_po", "Client PO"], ["supplier_quote", "Final Supplier Quote"], ["client_quote", "Our Client Quote"], ["costing", "Costing Snapshot"]];

const Row = ({ label, value, accent }) => (
  <div className="flex flex-col gap-0.5 py-1.5">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
    <span className={`text-sm ${accent ? "font-semibold text-[#1B4383]" : "text-slate-800"}`}>{value || "—"}</span>
  </div>
);

export default function SupplierPoDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shipment, setShipment] = useState(null);

  const load = useCallback(() => api.get(`/orders/supplier-pos/detail/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)), [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (d?.order_id && d.order_id !== "stock") api.get("/shipments").then((r) => setShipment((r.data || []).find((s) => s.order_id === d.order_id) || null)).catch(() => {});
  }, [d]);

  const viewFile = async (kind) => {
    try {
      const res = await api.get(`/orders/${d.order_id}/supplier-pos/${id}/file/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" }));
      window.open(url, "_blank"); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const maybeDeleteShipment = async (orderId) => {
    if (!orderId) return;
    try {
      const { data } = await api.get("/shipments");
      const ship = (data || []).find((s) => s.order_id === orderId);
      if (ship && window.confirm(`This order has a linked shipment ${ship.shipment_no}. Delete that shipment too?`)) {
        await api.delete(`/shipments/${ship.id}`);
        toast.success("Shipment deleted");
      }
    } catch (_) { /* best-effort */ }
  };

  const del = async () => {
    const issued = d.request?.status === "issued";
    const label = d.title || d.som_po_no || d.supplier_name || "this Somcore PO";
    const msg = issued
      ? `⚠️ ${label} has already been ISSUED to the supplier. Deleting it will remove the Somcore PO record. Delete anyway?`
      : `Delete Somcore PO ${label}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/orders/${d.order_id}/supplier-pos/${id}`);
      toast.success("Somcore PO deleted");
      await maybeDeleteShipment(d.order_id);
      nav(`/orders/${d.order_id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <AppLayout title="Somcore PO"><div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div></AppLayout>;
  if (!d) return <AppLayout title="Somcore PO"><div className="text-sm text-muted-foreground">Not found.</div></AppLayout>;
  if (d.po_category === "stock") return <StockPoDetail d={d} reload={load} />;

  const cur = d.po_currency;
  const activeIdx = STEPS.findIndex(([k]) => k === d.status);
  const req = d.request || {};

  return (
    <AppLayout title={d.title || "Somcore PO"}>
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => nav("/orders?section=somcore")} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="sompo-back"><ArrowLeft className="h-4 w-4" />Back to Somcore PO's</button>
        <Button variant="outline" size="sm" onClick={del} data-testid="sompo-delete" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 className="mr-1.5 h-4 w-4" />Delete Somcore PO</Button>
      </div>

      {/* Header */}
      <div className="mb-4 rounded-xl border border-border bg-white p-6" data-testid="sompo-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              <span className="rounded bg-[#E6ECF5] px-2 py-0.5 text-[#1B4383]" data-testid="sompo-title-pill">{d.title || d.som_po_no || "SOM-PO"}</span>
              <span>Supplier Purchase Order</span>
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="sompo-title">{d.supplier_name || "Supplier PO"}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground">
              <span>Fulfils</span>
              <button onClick={() => d.order_id && nav(`/orders/${d.order_id}`)} data-testid="sompo-so-link" className="font-semibold text-[#1B4383] underline-offset-2 hover:underline">{d.order_no}</button>
              {d.client_name && <span>· {d.client_name}</span>}
              {d.rfq_code && <><span>·</span><span>Won RFQ</span><button onClick={() => d.rfq_id && nav(`/rfqs/${d.rfq_id}`)} data-testid="sompo-rfq-link" className="inline-flex items-center gap-1 font-semibold text-[#1B4383] underline-offset-2 hover:underline">{d.rfq_code}<ExternalLink className="h-3 w-3 opacity-70" /></button></>}
              {shipment && <><span>·</span><button onClick={() => nav(`/logistics/shipment/${shipment.id}`)} data-testid="sompo-shipment-link" className="inline-flex items-center gap-1 font-semibold text-[#1B4383] underline-offset-2 hover:underline"><Ship className="h-3.5 w-3.5" />{shipment.shipment_no}<ExternalLink className="h-3 w-3 opacity-70" /></button></>}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Somcore Cost</div>
            <div className="font-heading text-2xl font-bold text-slate-900">{money(cur, d.po_cost)}</div>
            {req.status === "issued" && <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">Issued by {req.issued_by || "Finance"}</div>}
          </div>
        </div>

        {/* Status stepper */}
        <div className="mt-5 flex flex-wrap gap-1.5" data-testid="sompo-status">
          {STEPS.map(([k, lbl], i) => (
            <span key={k} className={`rounded-full px-3 py-1 text-xs font-medium ${i === activeIdx ? "bg-[#1B4383] text-white" : i < activeIdx ? "bg-[#E6ECF5] text-[#1B4383]" : "bg-slate-100 text-slate-400"}`}>{lbl}</span>
          ))}
        </div>
      </div>

      {/* Attachments */}
      <div className="mb-4 rounded-xl border border-border bg-white p-5" data-testid="sompo-docs">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><ClipboardList className="h-4 w-4" />Purchase Order & Supporting Documents</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-lg border border-border bg-slate-50/40 p-3">
            <div className="min-w-0"><div className="text-xs font-semibold text-slate-700">Issued Somcore PO</div><div className="truncate text-[11px] text-muted-foreground">{d.po_file ? d.po_file.filename : "Not uploaded yet"}</div></div>
            {d.po_file && <Button size="sm" variant="outline" onClick={() => viewFile("po")} data-testid="sompo-view-po" className="h-7 border-[#1B4383]/30 px-2 text-[#1B4383] hover:bg-[#E6ECF5]"><FileText className="mr-1 h-3 w-3" />View</Button>}
          </div>
          {ATT.map(([k, lbl]) => {
            const a = d.attachments?.[k];
            return (
              <div key={k} className="flex items-center justify-between rounded-lg border border-border bg-slate-50/40 p-3">
                <div className="min-w-0"><div className="text-xs font-semibold text-slate-700">{lbl}</div><div className="truncate text-[11px] text-muted-foreground">{a ? a.filename : "Not attached"}</div></div>
                {a && <Button size="sm" variant="outline" onClick={() => viewFile(k)} data-testid={`sompo-view-${k}`} className="h-7 border-[#1B4383]/30 px-2 text-[#1B4383] hover:bg-[#E6ECF5]"><FileText className="mr-1 h-3 w-3" />View</Button>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Panels */}
      <div className="mb-4 grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-white p-5" data-testid="sompo-customer">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><User className="h-4 w-4" />Customer Side</div>
          <Row label="Customer" value={d.client_name} />
          <Row label="Customer PO # (Master)" value={d.client_po_ref} accent />
          <Row label="Created From RFQ" value={d.rfq_code} accent />
          <Row label="Customer PO Date" value={fmtDate(d.client_po_date)} />
          <Row label="Customer Delivery Date" value={fmtDate(d.delivery_date)} />
          <Row label="Selling Value" value={money(cur, d.po_value)} />
        </div>
        <div className="rounded-xl border border-border bg-white p-5" data-testid="sompo-supplier">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><Building2 className="h-4 w-4" />Supplier Side</div>
          <Row label="Supplier" value={d.supplier_name} />
          <Row label="SOM-PO Number" value={d.som_po_no} accent />
          <Row label="Requested By" value={req.requested_by} />
          <Row label="Requested On" value={fmtDate(req.requested_at)} />
          <Row label="Issued By" value={req.issued_by} />
          <Row label="Issued On" value={fmtDate(req.issued_at)} />
        </div>
        <div className="rounded-xl border border-border bg-white p-5" data-testid="sompo-meta">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><Package className="h-4 w-4" />Meta</div>
          <Row label="Owner" value={d.owner_name} />
          <Row label="Logistics Ref" value={d.logistics_ref} />
          {d.notes && <Row label="Notes" value={d.notes} />}
        </div>
      </div>

      {/* Line items */}
      {d.lines?.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border bg-white" data-testid="sompo-lines">
          <div className="border-b border-border bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Items Awarded To {d.supplier_name || "Supplier"}</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-slate-50 text-left text-xs text-slate-500">
                <th className="px-4 py-2.5 font-medium">Description</th>
                <th className="px-4 py-2.5 font-medium">Part / Spec</th>
                <th className="px-4 py-2.5 font-medium">Qty</th>
                <th className="px-4 py-2.5 font-medium">UOM</th>
                <th className="px-4 py-2.5 font-medium">Unit Price</th>
              </tr>
            </thead>
            <tbody>
              {d.lines.map((l, i) => (
                <tr key={i} className="border-b border-border last:border-0" data-testid={`sompo-line-${i}`}>
                  <td className="px-4 py-2.5 text-slate-800">{l.description || "—"}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{l.part_no || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-700">{l.quantity}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{l.uom}</td>
                  <td className="px-4 py-2.5 text-slate-700">{l.unit_price ? money(d.line_currency, l.unit_price) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppLayout>
  );
}
